// src/components/GhostLinkButton.js - Updated for consistent styling with regular links
import React, { useState, useEffect } from "react";
import { Ghost } from "lucide-react";

const GhostLinkButton = ({
  link,
  isUnlocked,
  themeClass,
  paletteColors,
  passwordInput,
  onPasswordChange,
  onPasswordSubmit,
  error,
  textColor,
  linkBackground,
  borderColor,
  linkOpacity = 1,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [animatePassword, setAnimatePassword] = useState(false);

  // Always call hooks at the top level.
  useEffect(() => {
    if (showPassword) {
      // Start the animation after a short delay so the container mounts first.
      const timer = setTimeout(() => {
        setAnimatePassword(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setAnimatePassword(false);
    }
  }, [showPassword]);

  const togglePasswordField = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onPasswordSubmit(link.id);
  };

  // Common link button style - using CSS classes for consistency
  // Only apply necessary inline styles that need to be dynamically computed
  const linkButtonStyle = {
    backgroundColor: linkBackground,
    color: textColor,
    opacity: linkOpacity,
  };

  // Container style for the overall ghost link (including button and password field)
  const containerStyle = {
    marginBottom: showPassword ? "0" : "var(--link-gap, 1rem)", // No extra spacing when password field showing
  };

  // Password field container - positioned relatively below the button
  const passwordFieldContainerStyle = {
    opacity: animatePassword ? 1 : 0,
    display: showPassword ? "block" : "none",
  };

  // Mobile detection for icon size
  const isSmallMobile = window.innerWidth <= 480;

  return (
    <div
      className={`${themeClass} ghost-link-container`}
      style={containerStyle}
    >
      {isUnlocked ? (
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`link-button ${link.imageUrl ? "has-image" : ""}`}
          style={linkButtonStyle}
        >
          {link.imageUrl && (
            <div
              className="link-image-container"
            >
              <img
                src={link.imageUrl}
                alt=""
                className="link-thumbnail"
              />
            </div>
          )}
          <span className="link-title">
            {link.title}
          </span>
          <Ghost
            size={isSmallMobile ? 14 : 16}
            className="link-ghost-icon"
          />
        </a>
      ) : (
        <>
          <button
            onClick={togglePasswordField}
            className={`ghost-button ${link.imageUrl ? "has-image" : ""}`}
            style={linkButtonStyle}
          >
            {link.imageUrl && (
              <div
                className="link-image-container"
              >
                <img
                  src={link.imageUrl}
                  alt=""
                  className="link-thumbnail"
                />
              </div>
            )}
            <span className="link-title">
              {link.title}
            </span>
            <Ghost
              size={isSmallMobile ? 14 : 16}
              className="link-ghost-icon"
            />
          </button>

          {/* Password field positioned with consistent spacing */}
          <div className="password-field-container" style={passwordFieldContainerStyle}>
            <form onSubmit={handleSubmit} className="ghost-form">
              <input
                type="text"
                className="ghost-input"
                placeholder="Enter password"
                value={passwordInput || ""}
                onChange={(e) => onPasswordChange(link.id, e.target.value)}
                maxLength={6}
                autoFocus
              />
              <button type="submit" className="ghost-submit-button">
                <Ghost size={isSmallMobile ? 14 : 16} />
              </button>
            </form>
            {error && <div className="ghost-error">{error}</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default GhostLinkButton;

